import type {
  CierreFiscalDetail,
  CierreManualInputs,
  FixedAssetDto,
  FixedAssetInput,
} from "@fiscalo/shared-types";

import { apiClient } from "@/services/apiClient";

/** Year-end tax closing (cierre fiscal) for a tenant in the accountant cartera. */
export function getCierre(
  tenantId: string,
  fiscalYear: number,
): Promise<CierreFiscalDetail> {
  return apiClient
    .get<CierreFiscalDetail>(
      `/dashboard/accountant/tenants/${tenantId}/cierre/${fiscalYear}`,
    )
    .then((res) => res.data);
}

/** Patch the accountant's manual inputs for a closing (partial, per group). */
export function updateCierreInputs(
  tenantId: string,
  fiscalYear: number,
  inputs: CierreManualInputs,
): Promise<CierreFiscalDetail> {
  return apiClient
    .put<CierreFiscalDetail>(
      `/dashboard/accountant/tenants/${tenantId}/cierre/${fiscalYear}/inputs`,
      inputs,
    )
    .then((res) => res.data);
}

/** Download the cierre as a PDF report and trigger a browser save. */
export async function downloadCierrePdf(
  tenantId: string,
  fiscalYear: number,
): Promise<void> {
  const res = await apiClient.get(
    `/dashboard/accountant/tenants/${tenantId}/cierre/${fiscalYear}/pdf`,
    { responseType: "blob" },
  );
  const disposition = res.headers["content-disposition"] ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `Cierre fiscal ${fiscalYear}.pdf`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Finalize (lock) a closing — freezes a computed snapshot. */
export function finalizeCierre(
  tenantId: string,
  fiscalYear: number,
): Promise<CierreFiscalDetail> {
  return apiClient
    .post<CierreFiscalDetail>(
      `/dashboard/accountant/tenants/${tenantId}/cierre/${fiscalYear}/finalize`,
    )
    .then((res) => res.data);
}

export function createFixedAsset(
  tenantId: string,
  input: FixedAssetInput,
): Promise<FixedAssetDto> {
  return apiClient
    .post<FixedAssetDto>(
      `/dashboard/accountant/tenants/${tenantId}/fixed-assets`,
      input,
    )
    .then((res) => res.data);
}

export function updateFixedAsset(
  tenantId: string,
  assetId: string,
  input: FixedAssetInput,
): Promise<FixedAssetDto> {
  return apiClient
    .put<FixedAssetDto>(
      `/dashboard/accountant/tenants/${tenantId}/fixed-assets/${assetId}`,
      input,
    )
    .then((res) => res.data);
}

export function deleteFixedAsset(
  tenantId: string,
  assetId: string,
): Promise<void> {
  return apiClient
    .delete(`/dashboard/accountant/tenants/${tenantId}/fixed-assets/${assetId}`)
    .then(() => undefined);
}
